<script setup lang="ts">
import type { AddressBookAddressItem } from '@/composables/data/useAddressBookViewData';

import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import Sortable from 'sortablejs';

import KBtn from '@/components/btns/KBtn.vue';
import PaginationBtn from '@/components/btns/PaginationBtn.vue';
import PaginationSelectField from '@/components/fields/PaginationSelectField.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  addresses?: AddressBookAddressItem[];
  showDefaultToggle?: boolean;
  reorderDisabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  addresses: () => [],
  showDefaultToggle: false,
  reorderDisabled: false,
});

const emit = defineEmits<{
  editClick: [address: AddressBookAddressItem];
  deleteClick: [address: AddressBookAddressItem];
  setDefaultClick: [address: AddressBookAddressItem];
  reorder: [id: number, newIndex: number];
}>();

const DRAG_HANDLE_CLASS = 'address-book-drag-handle';

// Legacy shows "Room / Level / Unit" and the street line as one column
function streetAddress(address: AddressBookAddressItem): string {
  return [address.line1, address.line2].filter(Boolean).join(' ');
}

const pageSizeItems = [10, 25, 50];
const page = ref(1);
const pageSize = ref(10);

const total = computed(() => props.addresses.length);
const pagesCount = computed(() =>
  Math.max(1, Math.ceil(total.value / pageSize.value)),
);
const pageOffset = computed(() => (page.value - 1) * pageSize.value);
const pagedAddresses = computed(() =>
  props.addresses.slice(pageOffset.value, pageOffset.value + pageSize.value),
);
const rangeText = computed(() => {
  const from = pageOffset.value + 1;
  const to = Math.min(pageOffset.value + pageSize.value, total.value);
  return `${from}-${to} of ${total.value}`;
});

watch([total, pageSize], () => {
  if (page.value > pagesCount.value) page.value = pagesCount.value;
});

function handlePageSizeUpdate(value: number): void {
  pageSize.value = value;
  page.value = 1;
}

const tbodyRef = useTemplateRef('tbody');
let sortable: Sortable | null = null;

watch(tbodyRef, (el) => {
  sortable?.destroy();
  sortable = null;
  if (!el) return;
  sortable = Sortable.create(el, {
    animation: 150,
    handle: `.${DRAG_HANDLE_CLASS}`,
    disabled: props.reorderDisabled,
    onEnd({ oldIndex, newIndex, item, from }) {
      if (oldIndex == null || newIndex == null || oldIndex === newIndex) {
        return;
      }
      // Undo Sortable's DOM move so Vue's v-for stays the source of truth; the
      // reorder handler re-renders the real order (optimistically, with rollback).
      from.removeChild(item);
      from.insertBefore(item, from.children[oldIndex] ?? null);
      emit(
        'reorder',
        pagedAddresses.value[oldIndex].id,
        pageOffset.value + newIndex,
      );
    },
  });
});

watch(
  () => props.reorderDisabled,
  (disabled) => {
    sortable?.option('disabled', disabled);
  },
);

onBeforeUnmount(() => {
  sortable?.destroy();
  sortable = null;
});
</script>

<template>
  <div :class="$style.addressBookTable" data-testid="addressBookTable">
    <table :class="[tableStyles.table, $style.table]">
      <thead>
        <tr>
          <th :class="$style.dragTh">Sort</th>
          <th>Stop name</th>
          <th>Street address</th>
          <th>Suburb</th>
          <th :class="$style.stateTh">State</th>
          <th :class="$style.postcodeTh">Postcode</th>
          <th :class="$style.actionsTh">Actions</th>
        </tr>
      </thead>
      <tbody v-if="!props.addresses.length">
        <tr>
          <td colspan="7" class="border-b-0 p-0">
            <TableEmptyText>No addresses</TableEmptyText>
          </td>
        </tr>
      </tbody>
      <tbody v-else ref="tbody">
        <tr v-for="address in pagedAddresses" :key="address.id">
          <td :class="$style.dragTd">
            <v-icon
              :class="[DRAG_HANDLE_CLASS, $style.dragHandle]"
              icon="mdi-drag-horizontal-variant"
              size="small"
              aria-label="Drag to reorder"
            />
          </td>
          <td>{{ address.name }}</td>
          <td>
            <EmDashPlaceholder :item="streetAddress(address)" />
          </td>
          <td>{{ address.suburb.name }}</td>
          <td :class="$style.stateTd">{{ address.suburb.state }}</td>
          <td :class="$style.postcodeTd">{{ address.suburb.postcode }}</td>
          <td :class="$style.actionsTd">
            <menu :class="$style.actionsMenuList">
              <li :class="$style.actionsMenuListItem">
                <KBtn
                  :class="$style.btn"
                  variant="text"
                  size="sm"
                  append-icon="mdi-pencil-outline"
                  @click="emit('editClick', address)"
                >
                  Edit
                </KBtn>
              </li>
              <li :class="$style.actionsMenuListItem">
                <KBtn
                  :class="$style.btn"
                  variant="text"
                  size="sm"
                  append-icon="mdi-delete-outline"
                  priority="danger"
                  @click="emit('deleteClick', address)"
                >
                  Delete
                </KBtn>
              </li>
              <li
                v-if="props.showDefaultToggle"
                :class="$style.actionsMenuListItem"
              >
                <KBtn
                  :class="$style.btn"
                  variant="text"
                  size="sm"
                  :append-icon="
                    address.is_default ? 'mdi-home' : 'mdi-home-outline'
                  "
                  :priority="address.is_default ? 'success' : 'text'"
                  @click="emit('setDefaultClick', address)"
                >
                  {{ address.is_default ? 'Default' : 'Set default' }}
                </KBtn>
              </li>
            </menu>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="props.addresses.length" :class="$style.footer">
      <span :class="$style.footerText">Rows per page</span>
      <div :class="$style.pageSizeSelect">
        <PaginationSelectField
          local
          hide-details
          :items="pageSizeItems"
          :model-value="pageSize"
          @update:model-value="handlePageSizeUpdate"
        />
      </div>
      <span :class="$style.footerText">{{ rangeText }}</span>
      <PaginationBtn
        :disabled="page <= 1"
        aria-label="Previous page"
        @click="page -= 1"
      >
        <v-icon icon="mdi-chevron-left" />
      </PaginationBtn>
      <PaginationBtn
        :disabled="page >= pagesCount"
        aria-label="Next page"
        @click="page += 1"
      >
        <v-icon icon="mdi-chevron-right" />
      </PaginationBtn>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.addressBookTable {
  width: 100%;

  .table > thead > tr > th,
  .table > tbody > tr > td {
    padding: 8px 12px;
  }
}
  .dragTh {
    width: 48px;
  }
  .dragTd {
    text-align: center;
  }
    .dragHandle {
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }
  .stateTh {
    width: 80px;
  }
  .stateTd {
    white-space: nowrap;
  }
  .postcodeTh {
    width: 100px;
  }
  .postcodeTd {
    white-space: nowrap;
  }
  .actionsTh {
    width: 240px;
  }
  .actionsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }
        .btn {
          :global {
            .v-btn__content {
              text-transform: none;
            }
          }
        }
  .footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 6px 12px;
  }
    .footerText {
      font-size: 0.875rem;
      color: colors.$color-text-secondary;
    }
    .pageSizeSelect {
      flex: 0 0 auto;
    }
</style>
